import { attributes, Collection, refTo } from '@type-r/models';
import { asCurrency, asTimelessDate, CollapsableSection, Column, Columns, FormInput, PrimaryButton, ReadOnly } from 'lqx-react';
import { useLinked, useModel } from '@type-r/react';
import * as React from 'react';
import { Fragment } from 'react';
import { CompanyModel, globalStore, PlatformCompany, RelationshipModel } from 'api';
import { UnresolvedRemittanceAdvice } from '../../api/unresolvedRemittanceAdvice';
import { PickCompany } from '../../bank-transactions/unresolved/pickCompany';
import { ResolvePanel } from '../../bank-transactions/unresolved/details';
import { ResolveRemittanceAdviceDialog } from './resolve';
import { ViewEmail } from '../viewEmail';
import { RemittanceDocumentLink } from 'common/activities/details/remittanceDocumentLink';

import { InvoiceLookupInput } from '../../bank-transactions/unresolved/invoiceLookupInput';
import { UnresolvedRelationship } from '../../api/unresolvedRelationship';
import { asTimelessLinkedDate } from 'src/app/pages/digitization/shared';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

const State = attributes({
    customer : refTo( PlatformCompany ),
    supplier : refTo( PlatformCompany ),
    resolving : refTo( Boolean )
});

export const RemittanceAdviceDetails = ({ remittanceAdvice, companies, isArchived } : {
    remittanceAdvice : UnresolvedRemittanceAdvice,
    companies : Collection<PlatformCompany>,
    isArchived : Boolean,
}) => {

    const state = useModel( State );

    React.useEffect(() => {
        if (remittanceAdvice.supplierPlatformCompanyId) {
            state.$.supplier.set(companies.get(remittanceAdvice.supplierPlatformCompanyId.toString()));
        }

        if (remittanceAdvice.customerPlatformCompanyId) {
            state.$.customer.set(companies.get(remittanceAdvice.customerPlatformCompanyId.toString()));
        }        
    }, []);

    const $unresolvedRelationship = useLinked<UnresolvedRelationship>(null);


    return (
        <ReadOnly.Provider value={false} >
            <Fragment>
                <CollapsableSection title="Details">
                    <Columns>
                        <Column>
                            <FormInput label="Remittance Amount" readOnly positive integer value={asCurrency(remittanceAdvice.totalInvoiceAmount, remittanceAdvice.currency)} />
                            <FormInput label="Currency" value={remittanceAdvice.currencyCode} />
                            <RemittanceDocumentLink label="Source file" remittanceDocument={remittanceAdvice.originalFileUrl} originalFileName={remittanceAdvice.originalFileName} />
                            { !isArchived ? 
                            <>
                                <InvoiceLookupInput 
                                    $unresolvedRelationship={$unresolvedRelationship} 
                                    $supplier={state.$.supplier} 
                                    $customer={state.$.customer} 
                                />
                                <PickCompany 
                                    placeholder="Select Supplier Company" 
                                    $value={ state.$.supplier } 
                                    companies={companies} 
                                    filter={company => !state.customer || company.id !== state.customer.id} 
                                    customer={ state.customer } /> 
                            </>:
                                undefined 
                            }
                        </Column>
                        <Column>
                            <FormInput label="Payment Execution Date" disabled required date $value={asTimelessLinkedDate(remittanceAdvice.$.executionDate)} />
                            <FormInput label="Estimated Payment Value Date" disabled date $value={asTimelessLinkedDate(remittanceAdvice.$.estimatedPaymentDate)} />
                            <FormInput label="" aria-label="Empty" />
                            { !isArchived ? 
                            <>
                                <FormInput label="" aria-label="Empty" />
                                <PickCompany placeholder="Select Customer Company" $value={ state.$.customer } companies={companies} filter={company => !state.supplier || company.id !== state.supplier.id} supplier={ state.supplier } /> 
                                
                                {   
                                    Can(UserEntitlementCatalogs.BankEntryWrite) && 
                                    <PrimaryButton 
                                        onClick={() => state.$.resolving.set(true)}
                                        disabled={!state.customer || !state.supplier}
                                    >
                                        Resolve
                                    </PrimaryButton>
                                }
                            </> : undefined
                            }
                        </Column>
                    </Columns>
                </CollapsableSection>

                { remittanceAdvice.emailMetadata ? 
                    <ViewEmail emailInfo={remittanceAdvice.emailMetadata} /> : undefined }
                

                { state.resolving ?
                    <ResolveRemittanceAdviceDialog remittanceAdvice={remittanceAdvice} $resolving={state.$.resolving } customer={ state.customer } supplier={ state.supplier } />
                : null }
            </Fragment>
        </ReadOnly.Provider>
    )
}
